// useRadioMode.js - REFACTORED
// Simplified coordinator that delegates to focused modules
// Following Rule 4: No over-engineering - keep it simple and focused
// Reduced from 803 lines to ~250 lines

import { useState, useEffect, useRef, useCallback } from 'react';
import { createAIRadioServices } from '../services/aiRadio';
import ambientAudioManager from '../services/ambientAudioManager';
import logger from '../services/logger';
import api from '../services/api';
import { API_BASE_URL } from '../config';

// Import extracted modules
// NOTE: useRadioRecommendations removed - replaced by schedule-based system
// NOTE: useRadioDJSpeech REMOVED - legacy system, all DJ speech now handled by placard system in PlayerContext

/**
 * Main Radio Mode hook - Coordinates between DJ speech and recommendations
 * Simplified to focus on state management and coordination only
 */
export const useRadioMode = (playerContext) => {
  const { state, addTracksToPlaylist, playShowOpen, setAvailableModules, setRadioStartTime, autoQueue } = playerContext;

  // Core radio state
  const [isActive, setIsActive] = useState(false);
  const [djSpeechEnabled, setDjSpeechEnabled] = useState(true); // DJ speech toggle
  const [debugMode, setDebugMode] = useState(false);
  const [debugLog, setDebugLog] = useState([]);

  // AI Radio services
  const [aiRadioServices] = useState(() => createAIRadioServices());

  // Recommendation timeout ref
  const recommendationTimeoutRef = useRef(null);

  /**
   * Debug logging function
   */
  const addDebugLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = { timestamp, message, type };

    if (debugMode) {
      setDebugLog(prev => [logEntry, ...prev.slice(0, 19)]);
    }

    const logMessage = `Radio Coordinator: ${message}`;
    switch (type) {
      case 'error':
        logger.error(logMessage);
        break;
      case 'warning':
        logger.warn(logMessage);
        break;
      case 'success':
        logger.success(logMessage);
        break;
      default:
        logger.debug(logMessage);
        break;
    }
  }, [debugMode]);

  // Recommendation system removed - replaced by schedule-based segments
  // Stub values to prevent crashes until schedule system is implemented
  const [engine, setEngine] = useState(null);
  const [status, setStatus] = useState({ state: 'idle', message: 'Schedule mode pending' });
  const lastAnalyzedContextRef = useRef(null);
  const isProcessingRef = useRef(false);

  const analyzePlaylistContext = useCallback(() => {
    return { totalTracks: state.playlist?.tracks?.length || 0 };
  }, [state.playlist]);

  const needsRecommendations = useCallback(() => null, []);
  const generateRecommendations = useCallback(() => {}, []);
  const triggerManual = useCallback(() => {
    addDebugLog('Manual trigger disabled - schedule system pending');
  }, [addDebugLog]);

  // DJ speech handled by placard system in PlayerContext
  const handleProgressUpdate = useCallback(() => {}, []);
  const shouldBlockUserAction = useCallback(() => ({ blocked: false }), []);

  /**
   * Engine initialization removed - schedule system will replace this
   */
  useEffect(() => {
    if (state.accessToken) {
      addDebugLog('Access token available - schedule system pending');
    }
  }, [state.accessToken, addDebugLog]);

  /**
   * Main radio logic - monitors playlist and triggers recommendations
   */
  useEffect(() => {
    if (!isActive || !engine) {
      // Clear any pending timeouts when inactive
      if (recommendationTimeoutRef.current) {
        clearTimeout(recommendationTimeoutRef.current);
        recommendationTimeoutRef.current = null;
      }
      return;
    }

    const context = analyzePlaylistContext();
    if (!context) {
      return;
    }

    const trigger = needsRecommendations(context);
    if (!trigger) {
      return;
    }

    // Clear any existing timeout
    if (recommendationTimeoutRef.current) {
      clearTimeout(recommendationTimeoutRef.current);
      recommendationTimeoutRef.current = null;
    }

    // Determine delay based on trigger priority
    let delay = 1000; // Default 1 second

    if (trigger.priority === 'urgent') {
      delay = 500;
    } else if (trigger.priority === 'high') {
      delay = 800;
    } else if (trigger.priority === 'low') {
      delay = 2000;
    }

    // Schedule recommendation generation
    recommendationTimeoutRef.current = setTimeout(() => {
      generateRecommendations(context, trigger);
      recommendationTimeoutRef.current = null;
    }, delay);

  }, [
    isActive,
    engine,
    state.currentTrackIndex,
    state.playlist.tracks.length,
    state.contextVersion,
    state.isPlaying,
    analyzePlaylistContext,
    needsRecommendations,
    generateRecommendations
  ]);

  /**
   * Toggle radio mode on/off
   */
  const toggle = useCallback(() => {
    const newState = !isActive;
    setIsActive(newState);

    if (newState) {
      addDebugLog('🎙️ AI Radio activated');

      // Set radio start time for session duration tracking
      if (setRadioStartTime) {
        setRadioStartTime(Date.now());
      }

      // Fetch available modules from backend, pass to PlayerContext, start ambient
      (async () => {
        try {
          const result = await api.getModules();
          if (result.success && result.modules) {
            if (setAvailableModules) {
              setAvailableModules(result.modules);
            }
            addDebugLog(`📦 Loaded ${result.modules.length} modules`);

            // Initialize ambient flavor system (hot mic, ad libs, producer fill)
            ambientAudioManager.init(result.modules);
            addDebugLog('🎧 Ambient flavor system initialized');
          }
        } catch (e) {
          addDebugLog(`⚠️ Module fetch failed: ${e.message}`, 'warning');
        }
      })();

      // Start auto-queue behavior sync interval
      if (autoQueue) {
        autoQueue.startSyncInterval();
        addDebugLog('📊 Auto-queue behavior sync started');

        // If taste profile might be empty, import from Spotify first
        (async () => {
          try {
            const profileResult = await api.getTasteProfile();
            if (profileResult.success && !profileResult.profile?.has_vector) {
              addDebugLog('📥 Empty taste profile — importing from Spotify');
              await autoQueue.importProfile();
            }

            // Low-track fill is handled by checkQueue on the first progress tick
            // with fresh state — don't call generateMore() here with stale closures
          } catch (e) {
            addDebugLog(`⚠️ Auto-queue init error: ${e.message}`, 'warning');
          }
        })();
      }

      // Auto-place DJ segments throughout the playlist
      // NOTE: handled by PlayerContext.js useEffect initialization pass (avoids stale closure)

      // Play show open - pause music, DJ welcomes, then music resumes
      if (playShowOpen) {
        addDebugLog('🎤 Show open triggered - music gated');
        (async () => {
          try {
            await playShowOpen();
            addDebugLog('🎤 Show open finished - music resumed');
          } catch (e) {
            addDebugLog(`⚠️ Show open error: ${e.message}`, 'warning');
          }
        })();
      }

      // Low-track fill handled by checkQueue on first progress tick (fresh state)
    } else {
      addDebugLog('🔇 AI Radio deactivated');

      // Stop ambient flavor system
      ambientAudioManager.stop();
      addDebugLog('🔇 Ambient flavor system stopped');

      // Clear module state
      if (setAvailableModules) setAvailableModules([]);
      if (setRadioStartTime) setRadioStartTime(null);

      // Stop auto-queue sync and flush remaining behavior
      if (autoQueue) {
        autoQueue.stopSyncInterval();
        autoQueue.syncBehavior(); // Final flush
        addDebugLog('📊 Auto-queue sync stopped + flushed');
      }

      // Save DJ session memory to MongoDB
      (async () => {
        try {
          const supabaseToken = await api.getSupabaseToken();
          const spotifyToken = localStorage.getItem('spotify_access_token');
          await fetch(`${API_BASE_URL}/api/dj-session-end`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(spotifyToken && { 'Authorization': `Bearer ${spotifyToken}` }),
              ...(supabaseToken && { 'X-Supabase-Token': supabaseToken })
            },
            credentials: 'include'
          });
          addDebugLog('💾 DJ session saved');
        } catch (e) {
          addDebugLog(`⚠️ Session save failed: ${e.message}`, 'warning');
        }
      })();

      // Clean up recommendation system
      if (recommendationTimeoutRef.current) {
        clearTimeout(recommendationTimeoutRef.current);
        recommendationTimeoutRef.current = null;
      }

      isProcessingRef.current = false;
      lastAnalyzedContextRef.current = null;

      if (engine) {
        engine.reset();
      }
    }

    return newState;
  }, [isActive, addDebugLog, analyzePlaylistContext, generateRecommendations, engine, isProcessingRef, lastAnalyzedContextRef, playShowOpen, setAvailableModules, setRadioStartTime, autoQueue, state.playlist]);

  /**
   * Toggle DJ speech on/off
   */
  const toggleDJSpeech = useCallback(() => {
    const newState = !djSpeechEnabled;
    setDjSpeechEnabled(newState);

    if (newState) {
      addDebugLog('🎙️ DJ Speech enabled');
    } else {
      addDebugLog('🔇 DJ Speech disabled');
    }

    return newState;
  }, [djSpeechEnabled, addDebugLog]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (recommendationTimeoutRef.current) {
        clearTimeout(recommendationTimeoutRef.current);
      }
      // Stop ambient timers so clips don't fire after navigating away
      ambientAudioManager.stop();
    };
  }, []);

  // Public API - Clean and focused
  return {
    // Core state
    isActive,
    status,
    engine,

    // Core actions
    toggle,
    triggerManual,

    // DJ Speech controls
    djSpeechEnabled,
    toggleDJSpeech,

    // Debug
    debugMode,
    setDebugMode,
    debugLog,
    clearDebugLog: () => setDebugLog([]),

    // Context info
    getCurrentContext: analyzePlaylistContext,

    // DJ Speech API (placard system in PlayerContext handles actual speech)
    handleProgressUpdate,
    shouldBlockUserAction,

    // Service access (control manager for transition locking)
    services: aiRadioServices,
    getControlStatus: () => aiRadioServices.control.getStatus()
  };
};
